import z from "zod";
import api from "@/api";

// Shape of one entry from GET /v1/masterdata/vano-codes (agromate-api,
// TrialModelObserver::getVanoCodeCatalog()).
export const VanoCodeEntrySchema = z.object({
  field: z.string(),
  vano: z.string(),
  task: z.string(),
  label: z.string().nullable(),
  // The old system's Trial/Harvest/Report Activities (null = none of them). Optional, so a
  // catalog cached from before the field existed still validates.
  category: z.enum(["trial", "harvest", "report"]).nullish().transform((v) => v ?? null),
  stage: z.enum(["setup", "harvest", "report"]).nullable(),
  visible: z.boolean(),
  isInfo: z.boolean(),
});

// The 23 codes currently defined in agromate-api's TrialModelObserver::$diaryTriggerFields
// (the source). There is no CI contract test between the two repos - this hardcoded set is
// the only runtime guard against catalog drift. Whoever extends the catalog in backend MUST
// update this list in the same coordinated deploy, or the frontend will deliberately refuse
// to start (that's the point, not a bug).
const EXPECTED_VANO_CODES = [
  "A10", "A11", "A12", "A13", "A14", "A20", "A29", "A30", "A31", "A32", "A33",
  "A34", "A35", "A36", "A37", "A38", "A39", "A40", "A41", "A42", "A43", "A44", "A45",
];

// Each entry's shape is validated strictly. Count is deliberately not locked (a future 24th
// code doesn't fail this) - but all 23 currently-known codes must be present, or the catalog
// is treated as corrupt/incomplete.
export const VanoCodesResponseSchema = z
  .array(VanoCodeEntrySchema)
  .superRefine((entries, ctx) => {
    const present = new Set(entries.map((e) => e.vano));
    const missing = EXPECTED_VANO_CODES.filter((code) => !present.has(code));
    if (missing.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `VA-code catalog is missing expected code(s): ${missing.join(", ")}`,
      });
    }
  });

// Thrown for both network failures and validation failures - VanoCodesProvider only needs to
// handle one error path, not two (an invalid response is treated the same as an API error).
export class VanoCodesFetchError extends Error {}

export async function fetchAndValidateVanoCodes() {
  let response;
  try {
    // no-cache: always ask the API (a 304 when unchanged), never a copy the browser kept from
    // before a deploy - the names and categories come from here
    response = await api.get("/masterdata/vano-codes", { headers: { "Cache-Control": "no-cache" } });
  } catch (err) {
    throw new VanoCodesFetchError(`Failed to fetch VA-code catalog: ${err.message}`);
  }

  const result = VanoCodesResponseSchema.safeParse(response.data);
  if (!result.success) {
    throw new VanoCodesFetchError(
      `VA-code catalog response failed validation: ${result.error.message}`
    );
  }
  return result.data;
}

/** The activity categories in the order they are shown, with their headings (the old system's). */
export const ACTIVITY_CATEGORIES = [
  { key: "trial", title: "Trial Activities" },
  { key: "harvest", title: "Harvest Activities" },
  { key: "report", title: "Report Activities" },
  { key: null, title: "Other" },
];

/**
 * The name of an activity field (e.g. "StOut" -> "Stacked Out") from the catalog - the one source
 * for these names. `fallback` for fields the catalog does not name (e.g. hidden ones).
 */
export function activityLabel(vanoCodes, field, fallback = field) {
  return (vanoCodes || []).find((entry) => entry.field === field)?.label ?? fallback;
}

/**
 * Overrides the `header` of any column in `cols` whose `accessorKey`/`id` matches a catalog
 * entry's `field`, using that entry's `label` (TrialView's text - the authoritative source).
 * Columns with no match, or whose matching entry has no label (visible: false entries), are
 * returned unchanged. Pure, no React, no JSX - deliberately kept independent of any component
 * so both PlanningTable.jsx and TableTabs.jsx can call the exact same logic instead of each
 * re-implementing it.
 */
export function applyVanoLabels(cols, vanoCodes) {
  const labelByField = Object.fromEntries(
    vanoCodes.filter((entry) => entry.label).map((entry) => [entry.field, entry.label])
  );
  return cols.map((col) => {
    const label = labelByField[col.accessorKey ?? col.id];
    return label ? { ...col, header: label } : col;
  });
}
